import * as d3 from 'd3';
import { DataRenderer } from './DataRenderer';
import { ChartData } from '../data/ChartData';
import { ChartRenderer } from './ChartRenderer';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { BandChartData } from 'components/chart/data/BandChartData';
import { GradientCreator } from 'components/chart/renderer/GradientCreator';

export class BandRenderer extends DataRenderer {
  private readonly className?: string;

  constructor(
    private gElement: d3.Selection<SVGGElement, unknown, null, undefined>,
    private data: ChartDataArray,
    private gradientCreator: GradientCreator,
    private index?: number,
    public secondaryAxis?: boolean,
    public removeOuterSpacing?: boolean
  ) {
    super();

    this.className = 'parallel-serie';
  }

  public render(x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, limit?: number) {
    const isWithoutIndex = !this.index && this.index !== 0;
    const containers = this.gElement
      .selectAll(this.getClassSelector())
      .data([this.data.getData(limit)])
      .enter()
      .append('g')
      .attr('class', this.className + (isWithoutIndex ? '' : ` ${this.className}-${this.index}`));

    // TODO: Remove duplicate code
    if (x.domain().length === 1) {
      // add a short line in case there is only 1 data point available
      containers
        .append('path')
        .attr('fill', 'none')
        .attr('class', () => (isWithoutIndex ? 'line' : `line chart-stroke-color-${this.index}`))
        .style('stroke', () => this.data.color || '')
        .attr('d', (d: ChartData[]) => {
          const fixY = y(d[0].value!);
          const fixX = x(d[0].label!)!;
          const lineData: { xVal: number }[] = [
            {
              xVal: fixX - 6,
            },
            {
              xVal: fixX + 6,
            },
          ];
          return d3
            .line<{ xVal: number }>()
            .x(
              (dataEle: { xVal: number }, i) =>
                dataEle.xVal + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing)
            )
            .y(() => fixY)(lineData);
        });
    } else {
      this.gradientCreator.createGradient(containers, 'bandGradient');

      containers
        .append('path')
        .attr('fill', 'url(#bandGradient)')
        .attr('class', () => (isWithoutIndex ? 'line ' : `line chart-stroke-color-${this.index}`))
        .style('stroke', () => 'none')
        .attr(
          'd',
          d3
            .area<BandChartData>()
            .x((d: BandChartData, i) => x(d.label!)! + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing))
            .y0((d: BandChartData) => y(d.lower!))
            .y1((d: BandChartData) => y(d.upper!))
        );
      containers
        .append('path')
        .attr('fill', 'none')
        .attr('class', () => (isWithoutIndex ? 'line ' : `line chart-stroke-color-${this.index}`))
        .style('stroke', () => this.data.color || '')
        .attr(
          'd',
          d3
            .line<BandChartData>()
            .x((d: BandChartData, i) => x(d.label!)! + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing))
            .y((d: BandChartData) => y(d.value!))
        );
    }
  }

  private getClassSelector() {
    const isWithoutIndex = !this.index && this.index !== 0;
    return `.${this.className}${isWithoutIndex ? '' : `.${this.className}-${this.index}`}`;
  }

  public renderGuides(x: d3.ScaleBand<string>, y: d3.ScaleLinear<number, number>, limit?: number) {
    const guideContainer = this.gElement
      .select(this.getClassSelector())
      .selectAll('dot')
      .data(this.data.getData(limit))
      .enter()
      .append('g')
      .attr('class', 'guide');
    guideContainer
      .append('circle')
      .attr('class', 'dot')
      .attr('r', 3.5)
      .attr('cx', (d, i) => x(d.label!)! + ChartRenderer.getBandSpacing(x, i, !!this.removeOuterSpacing))
      .attr('cy', d => y(d.value!));
  }

  public switchHighlight(index: number, highlight: boolean) {
    const targetNodes = this.gElement.select(this.getClassSelector()).selectAll('.guide').nodes() as HTMLElement[];

    targetNodes.forEach(node => node.classList.remove('show'));

    if (highlight) {
      if (targetNodes.length === 0) {
        return;
      }
      targetNodes[index].classList.add('show');
    }
  }
}
